import React from "react"

import { yupResolver } from "@hookform/resolvers/yup"
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import { useMutationAppleLoginStudent } from "api/reactQuery/mutations/appleLogin"
import { useMutationGoogleLoginStudent } from "api/reactQuery/mutations/googleLogin"
import { useMutationStudentLogin } from "api/reactQuery/mutations/students"
import { useQueryStudentCleverLogin } from "api/reactQuery/queries/studentCleverLogin"
import { AxiosError } from "axios"
import AppleButton from "components/common/appleButton"
import CleverButton from "components/common/button/cleverButton"
import CleverSSOLoader from "components/common/cleverSSOLoader"
import GoogleButton from "components/common/googleButton"
import OpenIdSSOLoaders from "components/common/openIdSSOLoaders"
import PasswordTextField from "components/common/textField/registerInput/passwordTextField"
import TextField from "components/form/common/field/textField"
import { useApple } from "hooks/apple"
import { useCleverSSO } from "hooks/cleverSSO/useCleverSSO"
import { useGoogle } from "hooks/google"
import usePostLoginNavigate from "hooks/postLoginNavigate/usePostLoginNavigate"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppDispatch } from "hooks/store"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { Link, useNavigate } from "react-router-dom"
import { RoutePath } from "routes/Route.types"
import {
  setActivePageLogin,
  setActivePageSignUp,
  setAuthData,
} from "store/auth/auth.slice"
import { UserRole } from "ts/enums/User"
import { getErrorMessage, getErrorMessageFromTab } from "utils/api"

import { defaultValues, validateSchema } from "./LoginAsStudent.config"

interface IUserAuth {
  username: string
  password: string
}

const LoginAsStudent = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const navigateAfterLogin = usePostLoginNavigate()
  const { showSnackbar } = useCustomSnackbar()
  const queryClient = useQueryClient()
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const { isCleverSSOLoading, handleCleverReset, handleCleverAuthSuccess } =
    useCleverSSO({ userType: "student" })
  const methods = useForm({
    resolver: yupResolver(validateSchema),
    defaultValues: defaultValues,
  })

  const handleSSOSuccess = (data: {
    data: { access_token: string; finalization_required: boolean }
  }) => {
    dispatch(
      setAuthData({
        token: data.data.access_token,
        finalized: !data.data.finalization_required,
        role: UserRole.STUDENT,
      }),
    )

    if (!data.data.finalization_required) {
      showSnackbar({ title: t("noAuthFlow.loginSuccessful") })
    } else {
      showSnackbar({ title: t("noAuthFlow.yourAccountHasBeenCreated") })
    }

    if (data.data.finalization_required) {
      navigate(RoutePath.SIGNUP, { state: "login" })
      dispatch(setActivePageSignUp(3))
    } else {
      navigateAfterLogin()
    }
  }

  const handleSSOError = (error: AxiosError) => {
    showSnackbar({ title: getErrorMessageFromTab(error), variant: "error" })
  }

  const { mutate: googleLogin } = useMutationGoogleLoginStudent({
    option: { onSuccess: handleSSOSuccess, onError: handleSSOError },
  })

  const { mutate: loginStudent, isLoading: isLoadingLoginStudent } =
    useMutationStudentLogin({
      options: {
        onSuccess: (data) => {
          queryClient.invalidateQueries("userMe")
          showSnackbar({
            title: t("noAuthFlow.loginSuccessful"),
          })
          dispatch(
            setAuthData({
              token: data.data.access_token,
              finalized: true,
              role: UserRole.STUDENT,
            }),
          )
          navigateAfterLogin()
        },
        onError: (error) => {
          showSnackbar({
            title: getErrorMessage(error),
            variant: "error",
          })
        },
      },
    })

  const { mutate: appleLogin } = useMutationAppleLoginStudent({
    option: { onSuccess: handleSSOSuccess, onError: handleSSOError },
  })

  const { refButton } = useGoogle({
    mutate: googleLogin,
  })

  const { signIn: appleSignIn } = useApple({ mutate: appleLogin })

  const { refetch: cleverLogin } = useQueryStudentCleverLogin()

  const onSubmit = (data: IUserAuth) => {
    loginStudent({
      password: data.password,
      username: data.username,
    })
  }

  return (
    <>
      <CleverSSOLoader
        isLoading={isCleverSSOLoading}
        userType="student"
        onReset={handleCleverReset}
        onAuthSuccess={handleCleverAuthSuccess}
      />
      <OpenIdSSOLoaders userType="student" />
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Stack textAlign="left">
            <Typography
              variant="h2"
              component="p"
              color="primary.main"
              fontWeight="600"
            >
              {t("noAuthFlow.student")}
            </Typography>
            <Typography
              component="h1"
              variant="body1"
              fontSize="24px"
              fontWeight="500"
              color="mockup.neutral50"
              sx={{ pt: 1, pb: 4 }}
            >
              {t("noAuthFlow.signIn")}
            </Typography>
            <GoogleButton text={t("noAuthFlow.signInGoogle")} ref={refButton} />
            <Box sx={{ mt: 2 }}>
              <AppleButton
                text={t("noAuthFlow.signInApple")}
                onClick={appleSignIn}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <CleverButton
                text="Sign in with Clever"
                onClick={() => {
                  cleverLogin()
                }}
              />
            </Box>
            <Typography
              textAlign="center"
              variant="subtitle1"
              color="mockup.grey80"
              py={2}
            >
              {t("defaultTranslations.or")}
            </Typography>
            <Box>
              <TextField
                name="username"
                type="text"
                label={t("noAuthFlow.userName")}
                sx={{ mb: 3 }}
              />
              <PasswordTextField />
            </Box>
            <Box sx={{ display: "flex", mt: "40px" }}>
              <Button
                sx={{ mr: "10px", maxWidth: "100px" }}
                variant="outlined"
                fullWidth
                onClick={() =>
                  (window.location.href = "https://www.classbank.com/")
                }
              >
                {t("noAuthFlow.back")}
              </Button>
              <Button type="submit" fullWidth disabled={isLoadingLoginStudent}>
                <Box component="span" mr={1}>
                  {isLoadingLoginStudent && (
                    <CircularProgress
                      style={{ color: "inherit" }}
                      size="16px"
                    />
                  )}
                </Box>
                <Typography>{t("noAuthFlow.signIn")}</Typography>
              </Button>
            </Box>
            <Typography
              textAlign="center"
              variant="body2"
              color="mockup.neutral0"
              sx={{ fontWeight: "500", mt: 2 }}
            >
              {t("noAuthFlow.dontHaveAccount")}
              <Link
                to={RoutePath.SIGNUP}
                onClick={() => dispatch(setActivePageLogin(1))}
                style={{
                  color: theme.palette.mockup.primary20,
                  paddingLeft: "5px",
                  fontWeight: "500px",
                }}
              >
                {t("noAuthFlow.signup")}
              </Link>
            </Typography>
          </Stack>
        </form>
      </FormProvider>
    </>
  )
}

export default LoginAsStudent
